// The arcade hall's wall TV: the briefing before the cabinets (frog on a
// springboard over a volcano, held by three supports), and after all three
// cabinets the verdict (each failed cabinet breaks one support) and the
// personal debrief built from the learner's own mistakes.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import type { ArcadeTvConfig, ScreenBox } from "../../scenario/types";
import { audio } from "../../lib/audio";
import { arcadeSfx } from "./sfx";
import { CrtOverlay, bigRects, cabinetRect, useViewport } from "./Kit";
import {
  FROG_MIN_SUPPORTS,
  FROG_SUPPORTS,
  GAME_ORDER,
  allDone,
  arcadeScenarioId,
  clearProgress,
  courseTotal,
  exitArcadeDevice,
  frogSaved,
  markBriefed,
  useArcadeProgress,
  type ArcadeGameId,
  type ArcadeProgress,
} from "./results";
import { ArcadeWelcome } from "./Welcome";
import {
  CHEVRON_L,
  CHEVRON_R,
  DOC_LENS,
  FROG,
  FROG_HEAD,
  FROG_HEAD_OFF,
  GHOST,
  INVADER_CYAN,
  INVADER_PINK,
  PixIcon,
  PixText,
  SCENE_H,
  SCENE_W,
  SpriteRects,
  TURKEY,
  type Sprite,
} from "./pixelart";
import "./arcade.css";
import "./arcadetv.css";
import "./welcome.css";

const GAME_NAMES: Record<ArcadeGameId, string> = {
  turkeyhunt: "TURKEY HUNT",
  spotfraud: "SPOT THE FRAUD",
  invaders: "CYBER INVADERS",
};

const GAME_SKILL: Record<ArcadeGameId, string> = {
  turkeyhunt: "answering security questions under time pressure",
  spotfraud: "comparing a document with the original before you trust it",
  invaders: "refusing dangerous files, domains and requests",
};

const GAME_ICON: Record<ArcadeGameId, Sprite> = {
  turkeyhunt: TURKEY,
  spotfraud: DOC_LENS,
  invaders: INVADER_PINK,
};

const DEFAULT_BRIEF = [
  "Welcome to the arcade. Three cabinets, three security skills.",
  "The frog stands on a springboard over a volcano. Three supports hold it up.",
  "Every cabinet you do not pass breaks one support. Keep at least two, and the frog is safe.",
];

const VOLCANO_URL = "/cdn/sim/arcade-hall/img/volcano.jpg";

const C_CYAN = "#35d0ff";
const C_YELLOW = "#ffd21f";
const C_PINK = "#ff4fb0";
const C_GREEN = "#4cf27a";

type Mode = "brief" | "verdict" | "debrief";
type VStage = "cards" | "breaking" | "result";

function initialMode(p: ArcadeProgress): Mode {
  return allDone(p) ? "verdict" : "brief";
}

function failedGames(p: ArcadeProgress): ArcadeGameId[] {
  return GAME_ORDER.filter((g) => (p.results[g]?.supportPenalty ?? 0) > 0);
}

type TvProps = {
  config: ArcadeTvConfig;
  screenBox: ScreenBox | null;
  onDone: () => void;
};

export function ArcadeTv(props: TvProps) {
  if (props.config.welcome) return <ArcadeWelcome welcome={props.config.welcome} voice={props.config.voice ?? "nova"} />;
  return <ArcadeTvScreen {...props} />;
}

function ArcadeTvScreen({ config, screenBox, onDone }: TvProps) {
  const sid = arcadeScenarioId();
  const progress = useArcadeProgress(sid);
  const vp = useViewport();
  // Text on the wall TV would be too small at laptop sizes: use the TV's own
  // rect only when it already fills most of the window.
  const tvRect = cabinetRect(vp, screenBox, 16 / 9);
  const rect = tvRect.width >= vp.w * 0.7 ? tvRect : bigRects(vp, 16 / 9).glass;
  const [mode, setMode] = useState<Mode>(() => initialMode(progress));
  // The TV is off when the learner arrives; the picture starts on their click.
  const [on, setOn] = useState(false);
  const voice = config.voice ?? "nova";
  const briefLines = config.briefLines?.length ? config.briefLines : DEFAULT_BRIEF;
  const savedLine = config.savedLine ?? "The frog is safe. Your supports held.";
  const lostLine = config.lostLine ?? "Game over. Too many supports broke, and the frog fell.";

  useEffect(() => {
    for (const l of briefLines) void audio.prefetchSpeech(l, { voice });
    void audio.prefetchSpeech(savedLine, { voice });
    void audio.prefetchSpeech(lostLine, { voice });
    return () => audio.cancelSpeech();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const leave = useCallback(() => {
    audio.cancelSpeech();
    exitArcadeDevice(onDone, "tv");
  }, [onDone]);

  if (!on) {
    return (
      <div className="arc-root atv-root atv-root--off">
        <button
          type="button"
          className="atv-power"
          style={{ left: tvRect.left, top: tvRect.top, width: tvRect.width, height: tvRect.height }}
          onClick={() => {
            arcadeSfx.unlock();
            audio.powerOn();
            setOn(true);
          }}
        >
          <span className="atv-power__btn" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
              <path d="M12 3v8" />
              <path d="M6.3 6.8a8 8 0 1 0 11.4 0" />
            </svg>
          </span>
          <span className="atv-power__label">SWITCH ON THE TV</span>
        </button>
        <button type="button" className="arc-exit atv-power__back" onClick={() => exitArcadeDevice(onDone, "tv")}>
          <span aria-hidden="true">&#x2190;</span> BACK
        </button>
      </div>
    );
  }

  return (
    <div className="arc-root atv-root">
      <div
        className="arc-glass atv-glass"
        style={{ left: rect.left, top: rect.top, width: rect.width, height: rect.height }}
      >
        <div className="atv-frame" aria-hidden="true" />
        {mode === "brief" && (
          <Briefing
            lines={briefLines}
            voice={voice}
            replay={progress.briefed}
            onStart={() => {
              arcadeSfx.unlock();
              arcadeSfx.coin();
              markBriefed(sid);
              leave();
            }}
          />
        )}
        {mode === "verdict" && (
          <Verdict
            progress={progress}
            voice={voice}
            savedLine={savedLine}
            lostLine={lostLine}
            onContinue={() => setMode("debrief")}
          />
        )}
        {mode === "debrief" && (
          <Debrief
            progress={progress}
            onFinish={leave}
            onReplayAll={() => {
              clearProgress(sid, true);
              leave();
            }}
            onReplayFrog={() => setMode("verdict")}
          />
        )}
        <CrtOverlay />
      </div>
    </div>
  );
}

/* ------------------------------------------------------- shared retro bits */

/** Title bar: neon stripes, a cyan invader on each side and the pixel title. */
function Header({ title }: { title: string }) {
  return (
    <div className="atv-head">
      <span className="atv-head__stripes" aria-hidden="true">
        <i />
        <i />
      </span>
      <PixIcon sprite={INVADER_CYAN} h={3.7} className="atv-head__inv" />
      <h2 className="atv-head__title">
        <PixText text={title} h={4.4} color={C_YELLOW} gradient={["#ffe45c", "#ff9f1a"]} shadow="#ff2d6f" shadowOff={0.8} />
      </h2>
      <PixIcon sprite={INVADER_CYAN} h={3.7} className="atv-head__inv" />
      <span className="atv-head__stripes is-r" aria-hidden="true">
        <i />
        <i />
      </span>
    </div>
  );
}

function RetroButton({
  label,
  onClick,
  primary,
  big,
  disabled,
}: {
  label: string;
  onClick: () => void;
  primary?: boolean;
  big?: boolean;
  disabled?: boolean;
}) {
  const h = big ? 2.1 : 1.45;
  return (
    <button
      type="button"
      className={`atv-btn${primary ? " is-primary" : ""}${big ? " is-big" : ""}`}
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
    >
      <PixText text={label} h={h} color={primary ? "#1b1404" : "#d9f4ff"} />
    </button>
  );
}

/** FROG SAFETY n/3 with one frog head per support (broken ones grey). */
function SafetyBox({ left }: { left: number }) {
  return (
    <div className="atv-neon atv-safety">
      <PixText text="FROG SAFETY" h={1.8} color={C_CYAN} />
      <PixText text={`${left}/${FROG_SUPPORTS}`} h={2.4} color={C_GREEN} />
      <span className="atv-safety__icons" aria-hidden="true">
        {Array.from({ length: FROG_SUPPORTS }, (_, i) => (
          <PixIcon key={i} sprite={i < left ? FROG_HEAD : FROG_HEAD_OFF} h={2.3} />
        ))}
      </span>
    </div>
  );
}

function Pic({ children }: { children: ReactNode }) {
  return <div className="atv-pic">{children}</div>;
}

/* ---------------------------------------------------------------- briefing */

function Briefing({
  lines,
  voice,
  replay,
  onStart,
}: {
  lines: string[];
  voice: string;
  replay: boolean;
  onStart: () => void;
}) {
  const [shown, setShown] = useState(0);
  useEffect(() => {
    let alive = true;
    (async () => {
      for (let i = 0; i < lines.length && alive; i++) {
        setShown(i + 1);
        await audio.speak(lines[i], { voice });
      }
    })();
    return () => {
      alive = false;
    };
  }, [lines, voice]);
  return (
    <div className="atv-brief">
      <Header title="ARCADE CHALLENGE" />
      <Pic>
        <FrogScene supports={FROG_SUPPORTS} broken={[]} fall={false} />
      </Pic>
      <div className="atv-brief__right">
        <SafetyBox left={FROG_SUPPORTS} />
        <ul className="atv-lines">
          {lines.map((l, i) => (
            <li key={l} className={i === shown - 1 ? "is-now" : undefined}>
              {l}
            </li>
          ))}
        </ul>
        <ul className="atv-games">
          {GAME_ORDER.map((g) => (
            <li key={g} className="atv-neon atv-game">
              <PixIcon sprite={GAME_ICON[g]} h={3.4} className="atv-game__ico" />
              <div className="atv-game__txt">
                <span>{GAME_NAMES[g]}</span>
                <em>{GAME_SKILL[g]}</em>
              </div>
            </li>
          ))}
        </ul>
      </div>
      <div className="atv-cta-row">
        <PixIcon sprite={CHEVRON_R} h={2.4} className="atv-chev" />
        <RetroButton big primary label={replay ? "BACK TO THE ARCADE" : "START ARCADE CHALLENGE"} onClick={onStart} />
        <PixIcon sprite={CHEVRON_L} h={2.4} className="atv-chev" />
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------- verdict */

function Verdict({
  progress,
  voice,
  savedLine,
  lostLine,
  onContinue,
}: {
  progress: ArcadeProgress;
  voice: string;
  savedLine: string;
  lostLine: string;
  onContinue: () => void;
}) {
  const failed = useMemo(() => failedGames(progress), [progress]);
  const saved = frogSaved(progress);
  const [stage, setStage] = useState<VStage>("cards");
  const [broken, setBroken] = useState<number[]>([]);
  const timers = useRef<number[]>([]);

  const clearTimers = () => {
    timers.current.forEach((t) => window.clearTimeout(t));
    timers.current = [];
  };
  const at = (ms: number, fn: () => void) => {
    timers.current.push(window.setTimeout(fn, ms));
  };

  const finish = useCallback(() => {
    clearTimers();
    setBroken(failed.map((_, i) => i));
    setStage("result");
    if (saved) arcadeSfx.fanfare();
    else {
      arcadeSfx.splash();
      arcadeSfx.gameOver();
    }
    void audio.speak(saved ? savedLine : lostLine, { voice });
  }, [failed, saved, savedLine, lostLine, voice]);

  useEffect(() => {
    clearTimers();
    setBroken([]);
    setStage("cards");
    let t = 2400;
    at(t, () => setStage("breaking"));
    failed.forEach((_, i) => {
      at(t + 300, () => arcadeSfx.ping());
      at(t + 900, () => arcadeSfx.creak());
      at(t + 1700, () => {
        arcadeSfx.snap();
        setBroken((b) => [...b, i]);
      });
      t += 2200;
    });
    at(t + 600, finish);
    return clearTimers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const left = FROG_SUPPORTS - broken.length;
  return (
    <div className="atv-verdict">
      <Header title="FINAL SCORE READY" />
      <div className="atv-cards">
        {GAME_ORDER.map((g) => {
          const r = progress.results[g];
          const pass = !!r?.passedCabinetThreshold;
          return (
            <div key={g} className={`atv-neon atv-card${pass ? " is-pass" : " is-fail"}`}>
              <div className="atv-card__n">{GAME_NAMES[g]}</div>
              <div className="atv-card__s">{r?.normalizedCoursePoints ?? 0}<small>/100</small></div>
              <div className="atv-card__r">{pass ? "SUPPORT HOLDS" : "SUPPORT BREAKS"}</div>
            </div>
          );
        })}
      </div>
      <Pic>
        <FrogScene supports={FROG_SUPPORTS} broken={broken} fall={stage === "result" && !saved} hop={stage === "result" && saved} />
      </Pic>
      <div className="atv-verdict__side">
        <SafetyBox left={left} />
        {stage === "result" ? (
          <>
            <div className={`atv-result${saved ? " is-saved" : " is-lost"}`}>
              {saved ? (
                <PixText text="FROG SAVED!" h={3} color={C_YELLOW} gradient={["#ffe45c", "#ff9f1a"]} shadow="#ff2d6f" shadowOff={0.7} />
              ) : (
                <PixText text={"GAME OVER \u2014 POOR FROG"} h={2.1} color={C_PINK} shadow="#4a0a3a" shadowOff={0.7} />
              )}
            </div>
            <RetroButton primary big label="SEE YOUR DEBRIEF" onClick={onContinue} />
          </>
        ) : (
          <>
            <p className="atv-note">
              {failed.length === 0
                ? "Every cabinet passed."
                : `${failed.length} cabinet${failed.length > 1 ? "s" : ""} not passed. The frog needs ${FROG_MIN_SUPPORTS} supports.`}
            </p>
            <RetroButton label="SKIP" onClick={finish} />
          </>
        )}
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------- debrief */

function Debrief({
  progress,
  onFinish,
  onReplayAll,
  onReplayFrog,
}: {
  progress: ArcadeProgress;
  onFinish: () => void;
  onReplayAll: () => void;
  onReplayFrog: () => void;
}) {
  const [review, setReview] = useState(false);
  const total = courseTotal(progress);
  const saved = frogSaved(progress);
  const failed = failedGames(progress);
  const mistakes = GAME_ORDER.flatMap((g) =>
    (progress.results[g]?.knowledgeMistakes ?? []).map((m) => ({ ...m, game: g })),
  );
  const takeaways = mistakes.slice(0, 4);
  const brokenIdx = failed.map((_, i) => i);
  return (
    <div className="atv-debrief">
      <Header title="YOUR DEBRIEF" />
      <Pic>
        <FrogScene supports={FROG_SUPPORTS} broken={brokenIdx} fall={!saved} hop={saved} still />
      </Pic>
      <div className="atv-neon atv-final">
        <span className="atv-final__l">
          <PixText text="FINAL" h={1.8} color={C_CYAN} />
          <b>{total}</b>
          <span className="atv-final__of">/{GAME_ORDER.length * 100}</span>
        </span>
        <span className={`atv-final__tag ${saved ? "is-saved" : "is-lost"}`}>{saved ? "FROG SAVED" : "FROG LOST"}</span>
      </div>
      {!review ? (
        <div className="atv-neon atv-take">
          {failed.length > 0 && (
            <p className="atv-note">
              Practise next: {failed.map((g) => GAME_SKILL[g]).join("; ")}.
            </p>
          )}
          {takeaways.length > 0 ? (
            <ul className="atv-tlist">
              {takeaways.map((m) => (
                <li key={`${m.game}-${m.topic}`}>
                  <b>{m.topic}</b> {m.lesson}
                </li>
              ))}
            </ul>
          ) : (
            <p className="atv-note">No knowledge gaps found. Keep checking before you click, open or approve.</p>
          )}
        </div>
      ) : (
        <div className="atv-neon atv-take atv-review">
          {GAME_ORDER.map((g) => {
            const list = progress.results[g]?.knowledgeMistakes ?? [];
            return (
              <div key={g} className="atv-review__g">
                <div className="atv-review__h">{GAME_NAMES[g]}</div>
                {list.length === 0 ? (
                  <p className="atv-note">Nothing missed.</p>
                ) : (
                  <ul className="atv-tlist">
                    {list.map((m) => (
                      <li key={m.topic}>
                        <b>{m.topic}</b> {m.lesson}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
      <div className="atv-actions">
        <RetroButton primary label="FINISH" onClick={onFinish} />
        <RetroButton label="REPLAY ALL" onClick={onReplayAll} />
        <RetroButton label={review ? "BACK" : "REVIEW MISSED"} onClick={() => setReview((v) => !v)} disabled={mistakes.length === 0} />
        <RetroButton label="REPLAY FROG" onClick={onReplayFrog} />
      </div>
    </div>
  );
}

/* --------------------------------------------------------------- the frog */

// Scene units are 8 image pixels (the picture is 128 x 96 units). The crater
// rim of volcano.jpg is an ellipse centred at (61.4, 53) with radii (19, 3.3).
const POST_X = [49, 61, 73];
const POST_TOP = 36;
const PLANK = { x: 18, y: 31, w: 90, h: 5 };

function postBottom(x: number): number {
  const dx = (x - 61.4) / 19;
  return 53 + 3.3 * Math.sqrt(Math.max(0, 1 - dx * dx)) + 0.2;
}

function FrogScene({
  supports,
  broken,
  fall,
  hop,
  still,
}: {
  supports: number;
  broken: number[];
  fall: boolean;
  hop?: boolean;
  /** No transitions or animation: show the end state at once. */
  still?: boolean;
}) {
  const xs = POST_X.slice(0, supports);
  return (
    <>
      <img className="atv-pic__img" src={VOLCANO_URL} alt="" draggable={false} />
      <svg
        className={`atv-frog${fall ? " is-fall" : ""}${hop ? " is-hop" : ""}${still ? " is-still" : ""}`}
        viewBox={`0 0 ${SCENE_W} ${SCENE_H}`}
        shapeRendering="crispEdges"
        aria-hidden="true"
      >
        {/* lava glow over the crater */}
        <ellipse className="atv-glow" cx="61.4" cy="53" rx="17" ry="3" fill="#ffb02e" />
        {/* supports: they stand on the crater rim */}
        {xs.map((x, i) => {
          const isBroken = broken.includes(i);
          const bot = postBottom(x);
          return (
            <g
              key={x}
              className={`atv-post${isBroken ? " is-broken" : ""}`}
              style={{ transformOrigin: `${x}px ${bot}px` }}
            >
              <rect x={x - 2.5} y={POST_TOP} width={5} height={bot - POST_TOP} fill="#2c1608" />
              <rect x={x - 1.5} y={POST_TOP} width={3} height={bot - POST_TOP} fill="#a2612b" />
              <rect x={x - 1.5} y={POST_TOP} width={1} height={bot - POST_TOP} fill="#d9a05a" />
              <rect x={x + 0.5} y={POST_TOP} width={1} height={bot - POST_TOP} fill="#7a4620" />
              <rect x={x - 3.5} y={bot - 1.5} width={7} height={2} fill="#2c1608" />
              <rect x={x - 2.5} y={bot - 1} width={5} height={1} fill="#8a5426" />
              <rect x={x - 3.5} y={POST_TOP} width={7} height={2} fill="#2c1608" />
              <rect x={x - 2.5} y={POST_TOP} width={5} height={1} fill="#8a5426" />
            </g>
          );
        })}
        {/* springboard */}
        <g className="atv-plank">
          <rect x={PLANK.x} y={PLANK.y} width={PLANK.w} height={PLANK.h} fill="#2c1608" />
          <rect x={PLANK.x + 1} y={PLANK.y + 1} width={PLANK.w - 2} height={1} fill="#e0a863" />
          <rect x={PLANK.x + 1} y={PLANK.y + 2} width={PLANK.w - 2} height={2} fill="#a2612b" />
          <rect x={PLANK.x + 1} y={PLANK.y + 3} width={PLANK.w - 2} height={1} fill="#7a4620" />
          {[26, 39, 52, 66, 79, 93].map((gx) => (
            <rect key={gx} x={gx} y={PLANK.y + 2} width={3} height={1} fill="#c98a49" />
          ))}
          <rect x={PLANK.x + 1} y={PLANK.y + 1} width={2} height={3} fill="#c98a49" />
        </g>
        {/* the frog */}
        <g className="atv-frog__body">
          <SpriteRects sprite={FROG} />
        </g>
        {/* ghost after the fall */}
        {fall && (
          <g className="atv-ghost">
            <SpriteRects sprite={GHOST} />
          </g>
        )}
      </svg>
    </>
  );
}
